import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { CountrySelect } from '@frontend/features/travel-services/components/CountrySelect';
import type { TravelerCaptureField, TravelerCaptureSource } from '@bandido/shared/types';
import type { ReviewField } from './review';

export const CaptureField = ({
  label,
  field,
  value,
  onChange,
  current,
  required = false,
  disabled,
  sources,
  onSource,
  hint,
  kind,
  verify = false,
}: {
  label: string;
  field?: TravelerCaptureField;
  value: ReviewField;
  onChange: (value: ReviewField) => void;
  current?: string | null;
  required?: boolean;
  disabled?: boolean;
  sources: TravelerCaptureSource[];
  onSource: (source: TravelerCaptureSource) => void;
  hint?: string;
  kind?: 'country' | 'documentType';
  verify?: boolean;
}) => {
  const { t } = useTranslation();
  const ids = new Set([...(field?.sourceIds ?? []), ...(field?.alternatives ?? []).flatMap((item) => item.sourceIds)]);
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        py: 1.5,
        ...(current && current !== value.value ? { borderLeft: 3, borderLeftColor: 'warning.main', pl: 1 } : {}),
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {!required && (
          <FormControlLabel
            label={t(verify ? 'copilot.capture.verifiedInclude' : 'copilot.capture.include')}
            sx={{ minWidth: 100, mr: 0 }}
            control={
              <Checkbox
                checked={value.selected}
                inputProps={{ 'aria-label': `${t('copilot.capture.include')} ${label}` }}
                disabled={disabled}
                onChange={(_, checked) => onChange({ ...value, selected: checked })}
              />
            }
          />
        )}
        {kind === 'country' ? (
          <CountrySelect
            fullWidth
            label={label}
            value={value.value}
            disabled={disabled}
            onChange={(code) => onChange({ ...value, value: code })}
            helperText={hint}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            label={label}
            value={value.value}
            disabled={disabled}
            select={kind === 'documentType'}
            onChange={(event) =>
              onChange({ ...value, value: event.target.value, ...(verify ? { selected: false } : {}) })
            }
            helperText={hint}
            inputProps={{ maxLength: 120 }}
            sx={{ '& .MuiInputBase-input': { fontSize: 16 } }}
          >
            {kind === 'documentType' &&
              [...new Set(['', 'passport', 'ci', 'dni', 'other', value.value])].map((type) => (
                <MenuItem key={type} value={type}>
                  {type ? t(`travelers.documents.types.${type}`, { defaultValue: type }) : '—'}
                </MenuItem>
              ))}
          </TextField>
        )}
      </Box>
      {field?.value && (
        <Typography variant="caption" display="block">
          {t('copilot.capture.detectedValue', { value: field.sourceText ?? field.value })}
        </Typography>
      )}
      {current && (
        <Typography variant="caption" display="block">
          {t('copilot.capture.currentValue', { value: current })}
        </Typography>
      )}
      {!field?.value && !field?.alternatives?.length && (
        <Typography color="text.secondary" variant="caption" display="block">
          {t('copilot.capture.notPresent')}
        </Typography>
      )}
      {verify && value.value && (
        <Typography color="text.secondary" variant="caption" display="block">
          {t('copilot.capture.verifySource')}
        </Typography>
      )}
      {((field?.value && field?.needsReview) || field?.alternatives?.length) && (
        <Typography color="warning.main" variant="caption" display="block">
          {t('copilot.capture.needsReview')}
        </Typography>
      )}
      {field?.alternatives?.map((alternative, index) => (
        <Button
          key={index}
          size="small"
          disabled={disabled}
          onClick={() => onChange({ value: alternative.value, selected: !verify })}
        >
          {t('copilot.capture.useAlternative', { value: alternative.value })}
        </Button>
      ))}
      {sources
        .filter((source) => ids.has(source.id))
        .map((source) => (
          <Button key={source.id} size="small" onClick={() => onSource(source)}>
            {t('copilot.capture.viewSource')}
          </Button>
        ))}
    </Box>
  );
};
